import type { DashboardWidget } from "../types/core"
import {
  DASHBOARD_GRID_COLUMNS,
  getRequestedWidgetWidth,
  getWidgetRowSpan,
} from "./dashboardWidgetSizing"

/**
 * Widgets flow into rows in order rather than being placed freely: the user
 * picks each widget's width and its place in the sequence, and this module
 * packs that sequence into rows that always span the full grid.
 *
 * `grid_w` stores the width the user asked for. `grid_x`, `grid_y` and
 * `grid_h` store the packed result, so both the sequence and the rendered
 * layout survive a reload; a widget whose row had columns left over renders
 * wider than its requested width.
 *
 * Everything here works on a widget list that is already in sequence order.
 * Lists coming out of storage go through `sortWidgetsByFlowOrder` first.
 */

type DashboardBreakpoint = "compact" | "medium" | "wide"

/** Container widths, in pixels, at which the flow re-packs. */
const DASHBOARD_BREAKPOINT_WIDTHS = {
  medium: 640,
  wide: 1024,
}

/** Widest a widget can be on a medium container without taking the whole row. */
const MEDIUM_BREAKPOINT_HALF = DASHBOARD_GRID_COLUMNS / 2

interface FlowWidget {
  widget: DashboardWidget
  /** Columns the widget occupies once its row has been filled. */
  columns: number
}

export interface FlowRow {
  widgets: FlowWidget[]
  /** Height of the row in grid rows: the tallest widget in it. */
  rowSpan: number
}

export function getDashboardBreakpoint(
  containerWidth: number,
): DashboardBreakpoint {
  if (containerWidth >= DASHBOARD_BREAKPOINT_WIDTHS.wide) {
    return "wide"
  }
  if (containerWidth >= DASHBOARD_BREAKPOINT_WIDTHS.medium) {
    return "medium"
  }
  return "compact"
}

/** Sequence order, read back from the positions written on the last save. */
export function sortWidgetsByFlowOrder(
  widgets: DashboardWidget[],
): DashboardWidget[] {
  return [...widgets].sort((a, b) => {
    const rowDelta = (a.grid_y ?? 0) - (b.grid_y ?? 0)
    if (rowDelta !== 0) {
      return rowDelta
    }
    return (a.grid_x ?? 0) - (b.grid_x ?? 0)
  })
}

/**
 * Requested width narrowed to what the breakpoint allows: at most two widgets
 * per row on a medium container, one on a compact one.
 */
function getBreakpointWidth(
  widget: DashboardWidget,
  breakpoint: DashboardBreakpoint,
): number {
  if (breakpoint === "compact") {
    return DASHBOARD_GRID_COLUMNS
  }

  const requested = getRequestedWidgetWidth(widget)

  if (breakpoint === "medium") {
    return requested <= MEDIUM_BREAKPOINT_HALF
      ? MEDIUM_BREAKPOINT_HALF
      : DASHBOARD_GRID_COLUMNS
  }

  return requested
}

/**
 * Grows the widgets in a row so they span every column, sharing the leftover
 * evenly and giving any indivisible remainder to the last widget.
 */
function fillRow(entries: FlowWidget[]): FlowRow {
  const used = entries.reduce((total, entry) => total + entry.columns, 0)
  const leftover = DASHBOARD_GRID_COLUMNS - used
  const rowSpan = entries.reduce(
    (tallest, entry) => Math.max(tallest, getWidgetRowSpan(entry.widget)),
    1,
  )

  if (leftover <= 0) {
    return { widgets: entries, rowSpan }
  }

  const share = Math.floor(leftover / entries.length)
  const remainder = leftover - share * entries.length
  const lastIndex = entries.length - 1

  return {
    widgets: entries.map((entry, index) => ({
      ...entry,
      columns: entry.columns + share + (index === lastIndex ? remainder : 0),
    })),
    rowSpan,
  }
}

/**
 * Packs an ordered widget sequence into rows that each span the full grid.
 */
export function packDashboardRows(
  orderedWidgets: DashboardWidget[],
  breakpoint: DashboardBreakpoint = "wide",
): FlowRow[] {
  const rows: FlowRow[] = []
  let current: FlowWidget[] = []
  let used = 0

  orderedWidgets.forEach((widget) => {
    const columns = getBreakpointWidth(widget, breakpoint)

    if (current.length > 0 && used + columns > DASHBOARD_GRID_COLUMNS) {
      rows.push(fillRow(current))
      current = []
      used = 0
    }

    current.push({ widget, columns })
    used += columns

    if (used >= DASHBOARD_GRID_COLUMNS) {
      rows.push(fillRow(current))
      current = []
      used = 0
    }
  })

  if (current.length > 0) {
    rows.push(fillRow(current))
  }

  return rows
}

/**
 * Rewrites every widget's packed position from its place in the sequence, and
 * returns the widgets in that same sequence. Run this after any change to the
 * sequence or to a requested width, then persist the result.
 */
export function applyFlowPositions(
  orderedWidgets: DashboardWidget[],
): DashboardWidget[] {
  const positioned: DashboardWidget[] = []
  let rowOffset = 0

  packDashboardRows(orderedWidgets).forEach((row) => {
    let columnOffset = 0

    row.widgets.forEach(({ widget, columns }) => {
      positioned.push({
        ...widget,
        grid_x: columnOffset,
        grid_y: rowOffset,
        grid_w: getRequestedWidgetWidth(widget),
        grid_h: getWidgetRowSpan(widget),
      })
      columnOffset += columns
    })

    rowOffset += row.rowSpan
  })

  return positioned
}

export function rowContaining(
  rows: FlowRow[],
  widgetId: string,
): FlowRow | undefined {
  return rows.find((row) =>
    row.widgets.some((entry) => entry.widget.id === widgetId),
  )
}

export function rowMemberIds(row: FlowRow): string[] {
  return row.widgets.map((entry) => entry.widget.id)
}
